import React from 'react';
import { Box, Typography, Card, Chip, Grid, alpha } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useSelector } from 'react-redux';

export const TabResumen = ({ resumen }: { resumen: any }) => {
  const { qualifications_stats } = useSelector((state: any) => state.ratingsDevices);

  const dailyMetrics = resumen?.dailyMetrics || [];
  const npsData = dailyMetrics.length > 0 ? dailyMetrics.map((d: any) => d.nps) : Array(30).fill(0);
  const csatData =
    dailyMetrics.length > 0 ? dailyMetrics.map((d: any) => d.csat) : Array(30).fill(0);
  const cesData = dailyMetrics.length > 0 ? dailyMetrics.map((d: any) => d.ces) : Array(30).fill(0);
  const xLabels =
    dailyMetrics.length > 0
      ? dailyMetrics.map((d: any) => d.label)
      : Array.from({ length: 30 }, (_, i) => `${i + 1} May`);

  const channelColors = ['#5DCC0A', '#673AB7', '#FF9800', '#FFC107', '#9E9E9E'];
  const totalRespuestas = (resumen?.responseChannels || []).reduce(
    (acc: number, curr: any) => acc + Number(curr.count),
    0
  );

  const statsCards = [
    { key: 'mala', label: 'Mala', emoji: '😡', color: '#FF4C51' },
    { key: 'regular', label: 'Regular', emoji: '😟', color: '#FF9800' },
    { key: 'buena', label: 'Buena', emoji: '😐', color: '#FFC107' },
    { key: 'muy buena', label: 'Muy Buena', emoji: '🙂', color: '#8BC34A' },
    { key: 'excelente', label: 'Excelente', emoji: '😍', color: '#5DCC0A' },
  ];

  return (
    <>
      {qualifications_stats && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#2e263d', mb: 2 }}>
            Distribución de Calificaciones
          </Typography>
          <Grid container spacing={2}>
            {statsCards.map((stat) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={stat.key}>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    bgcolor: alpha(stat.color, 0.04),
                    border: '1px solid',
                    borderColor: alpha(stat.color, 0.2),
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 24px ${alpha(stat.color, 0.15)}`,
                      borderColor: alpha(stat.color, 0.4),
                      bgcolor: '#fff',
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '2.8rem',
                      lineHeight: 1,
                      filter: `drop-shadow(0 4px 8px ${alpha(stat.color, 0.4)})`,
                    }}
                  >
                    {stat.emoji}
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 900, color: stat.color, lineHeight: 1.2 }}
                    >
                      {qualifications_stats[stat.key] || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              height: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 900, color: '#2e263d', letterSpacing: '-0.5px' }}
              >
                Desempeño de métricas
              </Typography>
              <Chip
                label="Últimos 30 días"
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha('#673AB7', 0.1),
                  color: '#673AB7',
                  borderRadius: 2,
                }}
              />
            </Box>
            <Box
              sx={{
                height: 260,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#fff',
                borderRadius: 2,
              }}
            >
              <LineChart
                xAxis={[
                  {
                    scaleType: 'point',
                    data: xLabels,
                    tickLabelStyle: { fontSize: 11, fill: '#888', fontWeight: 600 },
                  },
                ]}
                yAxis={[
                  {
                    tickLabelStyle: { fontSize: 11, fill: '#888', fontWeight: 600 },
                  },
                ]}
                series={[
                  {
                    data: npsData,
                    color: '#5DCC0A',
                    label: 'NPS',
                    curve: 'catmullRom',
                    showMark: false,
                  },
                  {
                    data: csatData,
                    color: '#673AB7',
                    label: 'CSAT',
                    curve: 'catmullRom',
                    showMark: false,
                  },
                  {
                    data: cesData,
                    color: '#FF9800',
                    label: 'CES',
                    curve: 'catmullRom',
                    showMark: false,
                  },
                ]}
                height={260}
                margin={{ left: 40, right: 20, top: 10, bottom: 25 }}
                slotProps={{
                  legend: {
                    direction: 'horizontal',
                    position: { vertical: 'top', horizontal: 'center' },
                  },
                }}
                sx={{
                  '& .MuiLineElement-root': {
                    strokeWidth: 3,
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  flex: 1,
                  bgcolor: alpha('#5DCC0A', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#5DCC0A', 0.2),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#5DCC0A', 0.05)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: '#5DCC0A',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  NPS
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#4ba804' }}>
                    {resumen?.averages?.nps !== null && resumen?.averages?.nps !== undefined
                      ? resumen.averages.nps > 0
                        ? `+${resumen.averages.nps}`
                        : resumen.averages.nps
                      : '-'}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: alpha('#5DCC0A', 0.8), fontWeight: 700, mt: 0.5 }}
                >
                  Excelente
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  flex: 1,
                  bgcolor: alpha('#673AB7', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#673AB7', 0.2),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#673AB7', 0.05)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: '#673AB7',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  CSAT
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#5e35b1' }}>
                    {resumen?.averages?.csat !== null && resumen?.averages?.csat !== undefined
                      ? `${resumen.averages.csat}%`
                      : '-'}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: alpha('#673AB7', 0.8), fontWeight: 700, mt: 0.5 }}
                >
                  Excelente
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  flex: 1,
                  bgcolor: alpha('#FF9800', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#FF9800', 0.2),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#FF9800', 0.05)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: '#FF9800',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  CES
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#f57c00' }}>
                    {resumen?.averages?.ces !== null && resumen?.averages?.ces !== undefined
                      ? resumen.averages.ces
                      : '-'}
                    <span style={{ fontSize: '0.9rem', color: alpha('#FF9800', 0.6) }}> / 5</span>
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: alpha('#FF9800', 0.8), fontWeight: 700, mt: 0.5 }}
                >
                  Bajo esfuerzo
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              height: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#2e263d', mb: 2 }}>
              Respuestas por canal
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box sx={{ width: 180, height: 220 }}>
                <PieChart
                  series={[
                    {
                      data: (resumen?.responseChannels || []).map((ch: any, idx: number) => ({
                        id: idx,
                        value: Number(ch.count),
                        color: channelColors[idx % channelColors.length],
                      })),
                      innerRadius: 55,
                      outerRadius: 80,
                      paddingAngle: 1,
                      cornerRadius: 2,
                    },
                  ]}
                  height={220}
                  width={180}
                  slotProps={{ legend: { hidden: true } as any }}
                  margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, pl: 2 }}>
                {(resumen?.responseChannels || []).map((ch: any, idx: number) => {
                  const count = Number(ch.count);
                  const pct = totalRespuestas > 0 ? Math.round((count / totalRespuestas) * 100) : 0;
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: channelColors[idx % channelColors.length],
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: '#2e263d', fontSize: '0.85rem' }}
                        >
                          {ch.channel}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                          {pct}%
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                        >
                          ({count}) {'>'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                {(!resumen?.responseChannels || resumen?.responseChannels.length === 0) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', mt: 2 }}
                  >
                    Sin datos
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
